import { useState, useEffect, useCallback } from 'react';
import { getOrganizations } from '../../../../services/admin';
import { distribute, validateCommit } from '../../../../services/batch-extractions';
import { ApiError } from '../../../../services/api';
import type { OrganizationSummary } from '../../../../types/admin';
import type {
  BatchDistribution,
  DistributionResult,
  DistributeRejection,
} from '../../../../types/batch-extraction';
import './DistributeModal.css';

type Props = {
  batchId: string;
  distributions: BatchDistribution[];
  onClose: () => void;
  onDistributed: () => void;
  /**
   * Reports record ids that the commit gate flagged as unresolved (either from
   * the pre-flight `validate-commit` or the `distribute` 400) so the review
   * table can highlight them. An empty array clears any prior highlight.
   */
  onUnresolvedRecords: (recordIds: string[]) => void;
  /**
   * Records the gate rejected for being marked JUCO with no JC year. Reported separately from
   * unresolved teams because the reviewer fixes them differently — a year to fill, not a team to
   * pick — and one combined list would not say which.
   */
  onJucoYearMissing: (recordIds: string[]) => void;
};

export const DistributeModal = ({
  batchId,
  distributions,
  onClose,
  onDistributed,
  onUnresolvedRecords,
  onJucoYearMissing,
}: Props) => {
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [distributing, setDistributing] = useState(false);
  const [result, setResult] = useState<DistributionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-org blocker surfaced by the pre-flight check or the distribute 400.
  const [blockedOrgName, setBlockedOrgName] = useState<string | null>(null);
  const [unresolvedCount, setUnresolvedCount] = useState(0);

  // Get IDs of orgs that have already received this batch
  const distributedOrgIds = new Set(
    distributions.filter((d) => d.status === 'completed').map((d) => d.organizationId)
  );

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getOrganizations({ pageNumber: 1, pageSize: 100 });
      setOrganizations(response.items);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleSelectAll = () => {
    const availableIds = organizations
      .filter((org) => !distributedOrgIds.has(org.id))
      .map((org) => org.id);
    setSelectedIds(new Set(availableIds));
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleToggle = (orgId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(orgId)) {
      newSelected.delete(orgId);
    } else {
      newSelected.add(orgId);
    }
    setSelectedIds(newSelected);
  };

  const orgNameById = useCallback(
    (orgId: string) => organizations.find((o) => o.id === orgId)?.name ?? orgId,
    [organizations]
  );

  const flagUnresolved = useCallback(
    (orgId: string, recordIds: string[]) => {
      setBlockedOrgName(orgNameById(orgId));
      setUnresolvedCount(recordIds.length);
      onUnresolvedRecords(recordIds);
    },
    [orgNameById, onUnresolvedRecords]
  );

  const handleDistribute = async () => {
    if (selectedIds.size === 0) return;

    const orgIds = Array.from(selectedIds);

    setDistributing(true);
    setError(null);
    setBlockedOrgName(null);
    setUnresolvedCount(0);
    onUnresolvedRecords([]);
    onJucoYearMissing([]);

    try {
      // Pre-flight commit gate [SpecFlow C5]: every selected org must have all
      // its committable records resolved before we attempt distribution.
      for (const orgId of orgIds) {
        const validation = await validateCommit(batchId, orgId);
        if (!validation.isValid) {
          flagUnresolved(orgId, validation.unresolvedRecordIds);
          // Absent on an API deployed before this gate existed — web and API ship as two
          // workflows, so never read .length off it directly.
          onJucoYearMissing(validation.jucoYearMissingRecordIds ?? []);
          setError(
            validation.message ||
              'Some records are not assigned to a team in this organization. Resolve them before distributing.'
          );
          return;
        }
      }

      const distributionResult = await distribute(batchId, {
        organizationIds: orgIds,
      });
      setResult(distributionResult);
      onDistributed();
    } catch (err) {
      // The distribute endpoint re-checks the gate server-side and returns a
      // 400 with the offending org + record ids if a record became unresolved
      // between pre-flight and commit (nothing is committed).
      if (err instanceof ApiError && err.statusCode === 400 && err.response) {
        const rejection = err.response as DistributeRejection;
        if (Array.isArray(rejection.jucoYearMissingRecordIds)
          && rejection.jucoYearMissingRecordIds.length > 0) {
          onJucoYearMissing(rejection.jucoYearMissingRecordIds);
        }
        if (Array.isArray(rejection.unresolvedRecordIds)) {
          flagUnresolved(rejection.organizationId, rejection.unresolvedRecordIds);
          setError(
            rejection.message ||
              'Distribution blocked: some records are not assigned to a team.'
          );
          return;
        }
      }
      console.error('Distribution failed:', err);
      setError(err instanceof Error ? err.message : 'Distribution failed');
    } finally {
      setDistributing(false);
    }
  };

  const availableOrgs = organizations.filter((org) => !distributedOrgIds.has(org.id));
  const alreadyDistributedOrgs = organizations.filter((org) => distributedOrgIds.has(org.id));

  return (
    <div className="distribute-modal__overlay" onClick={onClose}>
      <div className="distribute-modal" onClick={(e) => e.stopPropagation()}>
        <div className="distribute-modal__header">
          <h3 className="distribute-modal__title">Distribute to Organizations</h3>
          <button className="distribute-modal__close" onClick={onClose}>
            Close
          </button>
        </div>

        {result ? (
          <div className="distribute-modal__result">
            <div className="distribute-modal__result-summary">
              <h4>Distribution Complete</h4>
              <p>
                Successfully distributed to {result.successfulDistributions} of{' '}
                {result.totalOrganizations} organizations.
              </p>
            </div>
            <div className="distribute-modal__result-list">
              {result.results.map((r) => (
                <div
                  key={r.organizationId}
                  className={`distribute-modal__result-item ${r.success ? 'distribute-modal__result-item--success' : 'distribute-modal__result-item--failed'}`}
                >
                  <span className="distribute-modal__result-name">{r.organizationName}</span>
                  {r.success ? (
                    <span className="distribute-modal__result-stats">
                      {r.prospectsCreated} prospects, {r.teamsCreated} teams created
                    </span>
                  ) : (
                    <span className="distribute-modal__result-error">{r.errorMessage}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="distribute-modal__actions">
              <button className="distribute-modal__btn distribute-modal__btn--primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            {loading ? (
              <div className="distribute-modal__loading">Loading organizations...</div>
            ) : error && organizations.length === 0 ? (
              <div className="distribute-modal__error">{error}</div>
            ) : (
              <>
                {error && (
                  <div className="distribute-modal__block" role="alert">
                    <div className="distribute-modal__block-title">
                      {blockedOrgName
                        ? `Cannot distribute to ${blockedOrgName}`
                        : 'Distribution blocked'}
                    </div>
                    <div className="distribute-modal__block-message">{error}</div>
                    {unresolvedCount > 0 && (
                      <div className="distribute-modal__block-hint">
                        {unresolvedCount} record{unresolvedCount === 1 ? '' : 's'} highlighted
                        in the table need a team assignment.
                      </div>
                    )}
                  </div>
                )}
                <div className="distribute-modal__toolbar">
                  <button
                    className="distribute-modal__select-btn"
                    onClick={handleSelectAll}
                    disabled={availableOrgs.length === 0}
                  >
                    Select All ({availableOrgs.length})
                  </button>
                  <button
                    className="distribute-modal__select-btn"
                    onClick={handleDeselectAll}
                    disabled={selectedIds.size === 0}
                  >
                    Deselect All
                  </button>
                </div>

                <div className="distribute-modal__list">
                  {availableOrgs.length === 0 && alreadyDistributedOrgs.length === 0 && (
                    <div className="distribute-modal__empty">No organizations found</div>
                  )}

                  {availableOrgs.map((org) => (
                    <label
                      key={org.id}
                      className={`distribute-modal__org ${selectedIds.has(org.id) ? 'distribute-modal__org--selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(org.id)}
                        onChange={() => handleToggle(org.id)}
                      />
                      <div className="distribute-modal__org-info">
                        <span className="distribute-modal__org-name">{org.name}</span>
                        <span className="distribute-modal__org-sport">{org.sport}</span>
                      </div>
                    </label>
                  ))}

                  {alreadyDistributedOrgs.length > 0 && (
                    <>
                      <div className="distribute-modal__divider">Already Distributed</div>
                      {alreadyDistributedOrgs.map((org) => {
                        const dist = distributions.find(
                          (d) => d.organizationId === org.id && d.status === 'completed'
                        );
                        return (
                          <div key={org.id} className="distribute-modal__org distribute-modal__org--distributed">
                            <div className="distribute-modal__org-info">
                              <span className="distribute-modal__org-name">{org.name}</span>
                              <span className="distribute-modal__org-date">
                                {dist?.distributedAt
                                  ? new Date(dist.distributedAt).toLocaleDateString()
                                  : 'Distributed'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>

                <div className="distribute-modal__footer">
                  <span className="distribute-modal__selected-count">
                    {selectedIds.size} organization{selectedIds.size !== 1 ? 's' : ''} selected
                  </span>
                  <div className="distribute-modal__actions">
                    <button
                      className="distribute-modal__btn distribute-modal__btn--secondary"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      className="distribute-modal__btn distribute-modal__btn--primary"
                      onClick={handleDistribute}
                      disabled={selectedIds.size === 0 || distributing}
                    >
                      {distributing ? 'Distributing...' : 'Distribute'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
